/**
 * TableSelectionManager
 * 
 * Manages cell selection state and visual feedback for table editing.
 * Handles cell highlighting, row/column hover effects, and selection overlay.
 */

export class TableSelectionManager {
  private selectedCell: HTMLTableCellElement | null = null;
  private highlightOverlay: HTMLElement | null = null;

  /**
   * Select a cell and show visual feedback
   */
  selectCell(cell: HTMLTableCellElement, container: HTMLElement): void {
    this.clearSelection();
    this.selectedCell = cell;

    // Add selection class
    cell.classList.add('table-cell-selected');

    // Create highlight overlay
    const rect = cell.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    this.highlightOverlay = document.createElement('div');
    this.highlightOverlay.className = 'table-cell-highlight';
    this.highlightOverlay.style.cssText = `
      position: absolute;
      left: ${rect.left - containerRect.left}px;
      top: ${rect.top - containerRect.top}px;
      width: ${rect.width}px;
      height: ${rect.height}px;
      border: 2px solid #1976d2;
      pointer-events: none;
      z-index: 100;
    `;

    container.appendChild(this.highlightOverlay);
  }

  /**
   * Clear cell selection
   */
  clearSelection(): void {
    if (this.selectedCell) {
      this.selectedCell.classList.remove('table-cell-selected');
      this.selectedCell = null;
    }

    if (this.highlightOverlay) {
      this.highlightOverlay.remove();
      this.highlightOverlay = null;
    }
  }

  /**
   * Get currently selected cell
   */
  getSelectedCell(): HTMLTableCellElement | null {
    return this.selectedCell;
  }

  /**
   * Highlight a row on hover
   */
  highlightRow(rowIndex: number, table: HTMLTableElement): void {
    const rows = table.querySelectorAll('tr');
    const row = rows[rowIndex];
    if (row) {
      row.classList.add('table-row-hover');
    }
  }

  /**
   * Clear row highlight
   */
  clearRowHighlight(table: HTMLTableElement): void {
    const rows = table.querySelectorAll('tr');
    rows.forEach(row => row.classList.remove('table-row-hover'));
  }

  /**
   * Highlight a column on hover
   */
  highlightColumn(colIndex: number, table: HTMLTableElement): void {
    const rows = table.querySelectorAll('tr');
    rows.forEach(row => {
      const cell = row.children[colIndex];
      if (cell) {
        cell.classList.add('table-col-hover');
      }
    });
  }

  /**
   * Clear column highlight
   */
  clearColumnHighlight(table: HTMLTableElement): void {
    const cells = table.querySelectorAll('td, th');
    cells.forEach(cell => cell.classList.remove('table-col-hover'));
  }
}
